import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RotateCcw } from 'lucide-react';
import { clientTypeLabel } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { ActivityDecision, ClientType } from '../governedMcpTypes';
import {
  DASHBOARD_SECTIONS,
  DEFAULT_FILTERS,
  buildDashboardMetrics,
  compactCount,
  type DashboardFilters,
  type DashboardRange,
  type NamedCount,
} from '../dashboardAnalytics';
import PillTabs from '../../../../components/common/PillTabs';
import { ghostBtn } from '../components/GatewayUi';
import { compactTokens, formatTokens } from '../quotaUsage';
import { enrichDashboardSource } from '../syntheticDashboard';

const RANGES: { id: DashboardRange; label: string }[] = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
];

const MODEL_COLORS_LIGHT = ['#4f46e5', '#0ea5e9', '#7c3aed', '#059669', '#d97706', '#e11d48'];
const MODEL_COLORS_DARK = ['#818cf8', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#fb7185'];
const DECISION_COLORS: Record<ActivityDecision, { light: string; dark: string }> = {
  allowed: { light: '#059669', dark: '#34d399' },
  denied: { light: '#e11d48', dark: '#fb7185' },
  approval_required: { light: '#d97706', dark: '#fbbf24' },
  failed: { light: '#64748b', dark: '#94a3b8' },
};
const LIMIT_MIX_COLORS = {
  ok: { light: '#059669', dark: '#34d399' },
  warning: { light: '#d97706', dark: '#fbbf24' },
  exceeded: { light: '#e11d48', dark: '#fb7185' },
};

function cardClass(isLight: boolean): string {
  return `rounded-2xl p-4 ${
    isLight
      ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04]'
      : 'bg-neutral-900 ring-1 ring-white/10'
  }`;
}

function selectClass(isLight: boolean): string {
  return `h-8 min-w-[148px] rounded-lg border px-2 text-xs ${
    isLight ? 'bg-white border-neutral-200 text-neutral-700' : 'bg-neutral-950 border-neutral-700 text-neutral-200'
  }`;
}

function ChartTooltip({
  active,
  payload,
  label,
  isLight,
  formatValue = compactCount,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color?: string }[];
  label?: string;
  isLight: boolean;
  formatValue?: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-xl px-3 py-2 text-xs shadow-lg ${
        isLight ? 'bg-white ring-1 ring-black/10 text-neutral-700' : 'bg-neutral-800 ring-1 ring-white/10 text-neutral-200'
      }`}
    >
      {label ? <p className={`font-medium mb-1 ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</p> : null}
      {payload.map((item) => (
        <p key={item.name} className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
          {item.name}: <span className="tabular-nums font-medium">{formatValue(item.value)}</span>
        </p>
      ))}
    </div>
  );
}

function Section({
  id,
  n,
  title,
  hint,
  isLight,
  children,
}: {
  id: string;
  n: number;
  title: string;
  hint: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  const heading = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  return (
    <section id={id} className="scroll-mt-36 space-y-3">
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 h-6 min-w-6 px-1.5 rounded-md text-[11px] font-semibold tabular-nums inline-flex items-center justify-center ${
            isLight ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-900'
          }`}
        >
          {n}
        </span>
        <div>
          <h2 className={`text-sm font-semibold ${heading}`}>{title}</h2>
          <p className={`text-xs mt-0.5 ${muted}`}>{hint}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function MetricCard({
  label,
  value,
  hint,
  isLight,
}: {
  label: string;
  value: string;
  hint: string;
  isLight: boolean;
}) {
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  return (
    <div className={cardClass(isLight)}>
      <p className={`text-[11px] uppercase tracking-wide ${muted}`}>{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${title}`}>{value}</p>
      <p className={`mt-0.5 text-xs ${muted}`}>{hint}</p>
    </div>
  );
}

function MiniEmpty({ title, description, isLight }: { title: string; description: string; isLight: boolean }) {
  return (
    <div className={`${cardClass(isLight)} py-10 text-center`}>
      <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</p>
      <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{description}</p>
    </div>
  );
}

function ChartCard({
  title,
  hint,
  isLight,
  children,
}: {
  title: string;
  hint: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  const heading = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  return (
    <div className={cardClass(isLight)}>
      <p className={`text-sm font-medium ${heading}`}>{title}</p>
      <p className={`text-xs mt-0.5 mb-3 ${muted}`}>{hint}</p>
      {children}
    </div>
  );
}

function Donut({
  data,
  colors,
  isLight,
  center,
  hint,
}: {
  data: NamedCount[];
  colors: string[];
  isLight: boolean;
  center: string;
  hint: string;
}) {
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const slices = data.filter((row) => row.value > 0);
  const total = data.reduce((sum, row) => sum + row.value, 0) || 1;
  return (
    <>
      <div className="relative h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius={56} outerRadius={78} paddingAngle={2} stroke="none">
              {slices.map((slice, index) => (
                <Cell key={slice.key} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip isLight={isLight} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className={`text-2xl font-semibold tabular-nums ${title}`}>{center}</p>
          <p className={`text-[11px] ${muted}`}>{hint}</p>
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {data.map((row, index) => (
          <li key={row.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 min-w-0">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: colors[index % colors.length] }} />
              <span className={`truncate ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>{row.name}</span>
            </span>
            <span className={`tabular-nums shrink-0 ${muted}`}>
              {compactCount(row.value)} · {Math.round((row.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function shortLabel(value: string): string {
  return value.length > 18 ? `${value.slice(0, 16)}…` : value;
}

export default function DashboardTab({
  isLight,
}: {
  isLight: boolean;
  onToast?: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory, catalogs } = useGovernedMcp();
  const [range, setRange] = useState<DashboardRange>('7d');
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const axis = isLight ? '#94a3b8' : '#737373';
  const grid = isLight ? '#f1f5f9' : '#27272a';
  const indigo = isLight ? '#4f46e5' : '#818cf8';
  const sky = isLight ? '#0ea5e9' : '#38bdf8';
  const teal = isLight ? '#0f766e' : '#2dd4bf';
  const amber = isLight ? '#d97706' : '#fbbf24';
  const rose = isLight ? '#e11d48' : '#fb7185';
  const modelColors = isLight ? MODEL_COLORS_LIGHT : MODEL_COLORS_DARK;
  const pageBg = isLight ? 'bg-slate-50' : 'bg-neutral-950';
  const xInterval = range === '30d' ? 4 : 0;

  const source = useMemo(
    () =>
      enrichDashboardSource({
        activity: state.activity,
        published: state.published,
        clients: state.clients,
        policies: state.policies,
        apiKeys: state.apiKeys,
        quotaAssignments: state.quotaAssignments,
        quotaUsages: state.quotaUsages,
        quotaDirectory,
        externalMcp: catalogs.externalMcp,
      }),
    [
      catalogs.externalMcp,
      quotaDirectory,
      state.activity,
      state.apiKeys,
      state.clients,
      state.policies,
      state.published,
      state.quotaAssignments,
      state.quotaUsages,
    ]
  );

  const metrics = useMemo(() => buildDashboardMetrics(range, filters, source), [filters, range, source]);

  useEffect(() => {
    logGovernedMcp('app', 'dashboard_synthetic_overlay', {
      activity: source.activity.length,
      users: source.quotaDirectory.users.length,
      usages: source.quotaUsages.length,
      clients: source.clients.length,
      published: source.published.length,
    });
  }, [
    source.activity.length,
    source.clients.length,
    source.published.length,
    source.quotaDirectory.users.length,
    source.quotaUsages.length,
  ]);

  const filtersDirty =
    range !== '7d' ||
    filters.departmentId !== 'all' ||
    filters.clientId !== 'all' ||
    filters.toolId !== 'all';

  const setRangeSafe = (next: DashboardRange) => {
    setRange(next);
    logGovernedMcp('app', 'dashboard_range_change', { range: next });
  };

  const patchFilter = (patch: Partial<DashboardFilters>, event: string) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      logGovernedMcp('app', event, next);
      return next;
    });
  };

  const resetFilters = () => {
    setRange('7d');
    setFilters(DEFAULT_FILTERS);
    logGovernedMcp('app', 'dashboard_filters_reset', {});
  };

  const decisionSlices: NamedCount[] = [
    { key: 'allowed', name: 'Allowed', value: metrics.audit.allowed },
    { key: 'denied', name: 'Denied', value: metrics.audit.denied },
    { key: 'approval_required', name: 'Approval required', value: metrics.audit.approvalRequired },
    { key: 'failed', name: 'Failed', value: metrics.audit.failed },
  ];
  const decisionTotal = decisionSlices.reduce((sum, row) => sum + row.value, 0);
  const decisionColors = decisionSlices.map((row) =>
    isLight ? DECISION_COLORS[row.key as ActivityDecision].light : DECISION_COLORS[row.key as ActivityDecision].dark
  );
  const clientTypeSlices = metrics.integrations.byType.map((row) => ({
    ...row,
    name: clientTypeLabel(row.key as ClientType),
  }));
  const toolChart = metrics.tools.slice(0, 8).map((row) => ({
    ...row,
    label: shortLabel(row.name.replace(/_/g, ' ')),
  }));
  const limitMixColors = metrics.limits.mix.map((row) => {
    const tone = LIMIT_MIX_COLORS[row.key as keyof typeof LIMIT_MIX_COLORS];
    return isLight ? tone.light : tone.dark;
  });
  const coverageColors = [indigo, sky, teal, isLight ? '#94a3b8' : '#a3a3a3'];
  const policyColors = [isLight ? '#059669' : '#34d399', rose, amber];
  const statusColors = [isLight ? '#059669' : '#34d399', amber, isLight ? '#94a3b8' : '#a3a3a3'];

  return (
    <div className="space-y-8">
      <div className={`sticky top-0 z-20 -mx-6 px-6 py-3 ${pageBg}`}>
        <div className={`${cardClass(isLight)} !p-3`}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-2.5">
              <label className="min-w-[132px]">
                <span className={`block text-[11px] font-medium mb-1 ${muted}`}>Range</span>
                <PillTabs
                  isLight={isLight}
                  ariaLabel="Range"
                  size="sm"
                  value={range}
                  items={RANGES}
                  onChange={setRangeSafe}
                />
              </label>
              <label>
                <span className={`block text-[11px] font-medium mb-1 ${muted}`}>Department</span>
                <select
                  className={selectClass(isLight)}
                  value={filters.departmentId}
                  onChange={(event) => patchFilter({ departmentId: event.target.value }, 'dashboard_department_filter')}
                >
                  <option value="all">All departments</option>
                  {source.quotaDirectory.departments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className={`block text-[11px] font-medium mb-1 ${muted}`}>AI client</span>
                <select
                  className={selectClass(isLight)}
                  value={filters.clientId}
                  onChange={(event) => patchFilter({ clientId: event.target.value }, 'dashboard_client_filter')}
                >
                  <option value="all">All clients</option>
                  {source.clients.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className={`block text-[11px] font-medium mb-1 ${muted}`}>Tool</span>
                <select
                  className={selectClass(isLight)}
                  value={filters.toolId}
                  onChange={(event) => patchFilter({ toolId: event.target.value }, 'dashboard_tool_filter')}
                >
                  <option value="all">All tools</option>
                  {source.published.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.mcpToolName || item.sourceName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`h-7 px-2.5 rounded-full text-[11px] font-medium ${
                  isLight ? 'bg-amber-50 text-amber-800' : 'bg-amber-500/10 text-amber-300'
                }`}
              >
                Sample volume included
              </span>
              <button type="button" className={ghostBtn(isLight)} onClick={resetFilters} disabled={!filtersDirty}>
                <RotateCcw size={13} />
                Reset
              </button>
            </div>
          </div>
        </div>
        <nav className="mt-3 flex flex-wrap gap-1.5" aria-label="Dashboard sections">
          {DASHBOARD_SECTIONS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`h-7 px-2.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 ${
                isLight
                  ? 'bg-white text-neutral-600 ring-1 ring-black/[0.04] hover:text-neutral-900'
                  : 'bg-neutral-900 text-neutral-300 ring-1 ring-white/10 hover:text-white'
              }`}
            >
              <span className="tabular-nums">{item.n}</span>
              {item.label}
            </a>
          ))}
        </nav>
      </div>

      <Section
        id="org-overview"
        n={1}
        title="Organization overview"
        hint={`${metrics.rangeLabel} · users, MCP calls, estimated tokens, and live integrations`}
        isLight={isLight}
      >
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <MetricCard
            label="Total users"
            value={compactCount(metrics.organization.totalUsers)}
            hint="Directory identities in this filter"
            isLight={isLight}
          />
          <MetricCard
            label="MCP calls"
            value={compactCount(metrics.organization.mcpCalls)}
            hint="Governed invocations in range"
            isLight={isLight}
          />
          <MetricCard
            label="Estimated tokens"
            value={compactTokens(metrics.organization.estimatedTokens)}
            hint={`${formatTokens(metrics.organization.estimatedTokens)} processed through Selfic`}
            isLight={isLight}
          />
          <MetricCard
            label="Active integrations"
            value={compactCount(metrics.organization.activeIntegrations)}
            hint={`${metrics.organization.activeClients} clients · ${metrics.organization.publishedTools} published tools`}
            isLight={isLight}
          />
        </div>
        <ChartCard title="Calls and estimated tokens" hint={metrics.rangeLabel} isLight={isLight}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={metrics.organization.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="dash-org-calls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={indigo} stopOpacity={isLight ? 0.28 : 0.4} />
                    <stop offset="100%" stopColor={indigo} stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="dash-org-tokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={sky} stopOpacity={isLight ? 0.22 : 0.32} />
                    <stop offset="100%" stopColor={sky} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={xInterval} minTickGap={16} />
                <YAxis yAxisId="calls" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={40} tickFormatter={(value: number) => compactCount(value)} />
                <YAxis
                  yAxisId="tokens"
                  orientation="right"
                  tick={{ fill: axis, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={46}
                  tickFormatter={(value: number) => compactTokens(value)}
                />
                <Tooltip content={<ChartTooltip isLight={isLight} />} />
                <Legend />
                <Area yAxisId="calls" type="monotone" dataKey="calls" name="Calls" stroke={indigo} fill="url(#dash-org-calls)" strokeWidth={1.5} />
                <Area yAxisId="tokens" type="monotone" dataKey="tokens" name="Tokens" stroke={sky} fill="url(#dash-org-tokens)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </Section>

      <Section
        id="user-analytics"
        n={2}
        title="User analytics"
        hint="Tool usage and estimated tokens across people in this filter. No employee roster."
        isLight={isLight}
      >
        {metrics.userAnalytics.withUsage === 0 ? (
          <MiniEmpty
            isLight={isLight}
            title="No usage in range"
            description="No tool calls or estimated tokens match this range and filter."
          />
        ) : (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
              <MetricCard
                label="People with usage"
                value={compactCount(metrics.userAnalytics.withUsage)}
                hint="Anyone with calls or tokens in range"
                isLight={isLight}
              />
              <MetricCard
                label="Avg calls"
                value={compactCount(metrics.userAnalytics.avgCalls)}
                hint="Per person with usage"
                isLight={isLight}
              />
              <MetricCard
                label="Avg tokens"
                value={compactTokens(metrics.userAnalytics.avgTokens)}
                hint="Estimated per person with usage"
                isLight={isLight}
              />
            </div>
            <div className="grid xl:grid-cols-12 gap-4">
              <div className="xl:col-span-7">
                <ChartCard title="People by token band" hint="Distribution of estimated tokens, not a roster" isLight={isLight}>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={metrics.userAnalytics.bands} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip isLight={isLight} />} />
                        <Bar dataKey="value" name="People" radius={[6, 6, 0, 0]} barSize={28} fill={indigo} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
              </div>
              <div className="xl:col-span-5">
                <ChartCard title="Token concentration" hint="Share held by the top 10% of users" isLight={isLight}>
                  <Donut
                    data={metrics.userAnalytics.concentration}
                    colors={[indigo, isLight ? '#c7d2fe' : '#312e81']}
                    isLight={isLight}
                    center={`${Math.round(
                      (metrics.userAnalytics.concentration[0].value /
                        Math.max(
                          1,
                          metrics.userAnalytics.concentration[0].value + metrics.userAnalytics.concentration[1].value
                        )) *
                        100
                    )}%`}
                    hint="top 10%"
                  />
                </ChartCard>
              </div>
            </div>
          </>
        )}
      </Section>

      <Section id="team-analytics" n={3} title="Team analytics" hint="Aggregate usage by department" isLight={isLight}>
        {metrics.teams.length === 0 ? (
          <MiniEmpty isLight={isLight} title="No team usage" description="No departments match this filter." />
        ) : (
          <div className="grid xl:grid-cols-12 gap-4">
            <div className="xl:col-span-7">
              <ChartCard title="Tokens and calls by team" hint="Department totals in this range" isLight={isLight}>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.teams} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={42} tickFormatter={(value: number) => compactCount(value)} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Legend />
                      <Bar dataKey="tokens" name="Tokens" radius={[6, 6, 0, 0]} barSize={14} fill={indigo} />
                      <Bar dataKey="calls" name="Calls" radius={[6, 6, 0, 0]} barSize={14} fill={sky} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
            <div className="xl:col-span-5">
              <ChartCard title="Headcount by team" hint="People in this filter" isLight={isLight}>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.teams} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={88} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Bar dataKey="headcount" name="People" radius={[0, 6, 6, 0]} barSize={14} fill={teal} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
          </div>
        )}
      </Section>

      <Section
        id="token-analytics"
        n={4}
        title="Token analytics"
        hint="Estimated tokens processed through Selfic. Input and output are not stored separately."
        isLight={isLight}
      >
        {metrics.tokens.total === 0 && metrics.tokens.monthlyUsed === 0 ? (
          <MiniEmpty isLight={isLight} title="No estimated tokens" description="No token usage is recorded for this range and filter." />
        ) : (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
              <MetricCard label="Estimated in range" value={compactTokens(metrics.tokens.total)} hint={metrics.rangeLabel} isLight={isLight} />
              <MetricCard
                label="This month"
                value={compactTokens(metrics.tokens.monthlyUsed)}
                hint={`of ${compactTokens(metrics.tokens.monthlyLimit)} assigned`}
                isLight={isLight}
              />
              <MetricCard label="Monthly share" value={`${metrics.tokens.monthlyShare}%`} hint="Used versus assigned limit" isLight={isLight} />
            </div>
            <div className="grid xl:grid-cols-12 gap-4">
              <div className="xl:col-span-8">
                <ChartCard title="Estimated tokens over time" hint={metrics.rangeLabel} isLight={isLight}>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={metrics.tokens.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="dash-token-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={indigo} stopOpacity={isLight ? 0.22 : 0.35} />
                            <stop offset="100%" stopColor={indigo} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={xInterval} minTickGap={16} />
                        <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={46} tickFormatter={(value: number) => compactTokens(value)} />
                        <Tooltip content={<ChartTooltip isLight={isLight} formatValue={compactTokens} />} />
                        <Area type="monotone" dataKey="value" name="Tokens" stroke={indigo} fill="url(#dash-token-fill)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
              </div>
              <div className="xl:col-span-4">
                <ChartCard title="By model" hint="Mix scaled to this range" isLight={isLight}>
                  {metrics.tokens.byModel.length === 0 ? (
                    <p className={`text-sm ${muted}`}>No model mix in this filter.</p>
                  ) : (
                    <Donut
                      data={metrics.tokens.byModel}
                      colors={modelColors}
                      isLight={isLight}
                      center={compactTokens(metrics.tokens.total)}
                      hint="tokens"
                    />
                  )}
                </ChartCard>
              </div>
            </div>
          </>
        )}
      </Section>

      <Section id="tool-analytics" n={5} title="MCP tool analytics" hint="Execution counts, popularity, errors, and latency" isLight={isLight}>
        {metrics.tools.length === 0 ? (
          <MiniEmpty isLight={isLight} title="No tool executions" description="No MCP tool calls were recorded for this range and filter." />
        ) : (
          <div className="grid xl:grid-cols-12 gap-4">
            <div className="xl:col-span-7">
              <ChartCard title="Executions, errors, and denials" hint="Top tools in this filter" isLight={isLight}>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={toolChart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Legend />
                      <Bar dataKey="executions" name="Executions" radius={[4, 4, 0, 0]} fill={indigo} />
                      <Bar dataKey="failures" name="Errors" radius={[4, 4, 0, 0]} fill={rose} />
                      <Bar dataKey="denials" name="Denied" radius={[4, 4, 0, 0]} fill={amber} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
            <div className="xl:col-span-5">
              <ChartCard title="Latency" hint="P50 and P95 by tool" isLight={isLight}>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={toolChart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                      <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={40} tickFormatter={(value: number) => `${value}`} />
                      <Tooltip content={<ChartTooltip isLight={isLight} formatValue={(value) => `${value} ms`} />} />
                      <Legend />
                      <Line type="monotone" dataKey="p50" name="P50" stroke={teal} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="p95" name="P95" stroke={amber} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
          </div>
        )}
      </Section>

      <Section
        id="usage-limits"
        n={6}
        title="Usage limits"
        hint="Monthly token-volume limits and per-tool rate limits. Daily call quotas are not stored."
        isLight={isLight}
      >
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <MetricCard label="Monthly limit" value={compactTokens(metrics.limits.monthlyLimit)} hint="Assigned in the current month" isLight={isLight} />
          <MetricCard
            label="Used"
            value={compactTokens(metrics.limits.monthlyUsed)}
            hint={`${compactTokens(metrics.limits.remaining)} remaining`}
            isLight={isLight}
          />
          <MetricCard
            label="At or over limit"
            value={compactCount(metrics.limits.overLimit)}
            hint={`${metrics.limits.warning} approaching 80%`}
            isLight={isLight}
          />
          <MetricCard
            label="Rate-limited tools"
            value={`${metrics.limits.rateLimitedTools}/${metrics.limits.publishedTools}`}
            hint="Published tools with a per-minute cap"
            isLight={isLight}
          />
        </div>
        <div className="grid xl:grid-cols-12 gap-4">
          <div className="xl:col-span-7">
            <ChartCard title="Used versus remaining" hint="Current month token volume" isLight={isLight}>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[{ name: 'This month', Used: metrics.limits.monthlyUsed, Remaining: metrics.limits.remaining }]}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={46} tickFormatter={(value: number) => compactTokens(value)} />
                    <Tooltip content={<ChartTooltip isLight={isLight} formatValue={compactTokens} />} />
                    <Legend />
                    <Bar dataKey="Used" stackId="cap" fill={indigo} barSize={48} />
                    <Bar dataKey="Remaining" stackId="cap" fill={isLight ? '#c7d2fe' : '#312e81'} radius={[6, 6, 0, 0]} barSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>
          <div className="xl:col-span-5">
            <ChartCard title="Limit health" hint="Assignments on track, warning, or over" isLight={isLight}>
              <Donut
                data={metrics.limits.mix}
                colors={limitMixColors}
                isLight={isLight}
                center={compactCount(metrics.limits.mix.reduce((sum, row) => sum + row.value, 0))}
                hint="assignments"
              />
            </ChartCard>
          </div>
        </div>
      </Section>

      <Section id="bulk-coverage" n={7} title="Bulk user management" hint="How limits and access policies currently cover people" isLight={isLight}>
        {metrics.coverage.totalEmployees === 0 ? (
          <MiniEmpty isLight={isLight} title="No people in scope" description="The directory has no people in this department, client, or tool filter." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <ChartCard title="Limit assignment" hint={`${metrics.coverage.totalEmployees} people in this filter`} isLight={isLight}>
              <Donut
                data={metrics.coverage.limitSource}
                colors={coverageColors}
                isLight={isLight}
                center={compactCount(metrics.coverage.totalEmployees)}
                hint="people"
              />
            </ChartCard>
            <ChartCard title="Policy coverage" hint="Included in at least one active access policy" isLight={isLight}>
              <Donut
                data={metrics.coverage.policy}
                colors={[isLight ? '#059669' : '#34d399', indigo]}
                isLight={isLight}
                center={`${Math.round(
                  ((metrics.coverage.policy[0]?.value || 0) / Math.max(metrics.coverage.totalEmployees, 1)) * 100
                )}%`}
                hint="covered"
              />
            </ChartCard>
          </div>
        )}
      </Section>

      <Section id="access-policies" n={8} title="Access policies" hint="Allow or deny coverage for MCP tools" isLight={isLight}>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <MetricCard label="Allow" value={compactCount(metrics.policies.allow)} hint="Permit policies" isLight={isLight} />
          <MetricCard label="Deny" value={compactCount(metrics.policies.deny)} hint="Block policies" isLight={isLight} />
          <MetricCard
            label="Tools named"
            value={`${metrics.policies.namedTools}/${metrics.policies.namedTools + metrics.policies.unnamedTools}`}
            hint={`${metrics.policies.unnamedTools} tools with no named policy`}
            isLight={isLight}
          />
          <MetricCard
            label="Denied calls"
            value={compactCount(metrics.policies.deniedCalls)}
            hint={`${metrics.rangeLabel} · ${metrics.policies.active} active policies`}
            isLight={isLight}
          />
        </div>
        {metrics.policies.allow + metrics.policies.deny + metrics.policies.requireApproval === 0 ? (
          <MiniEmpty isLight={isLight} title="No access policies" description="No allow or deny policies are defined yet." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <ChartCard title="Policy effect" hint="Allow, deny, and approval rules" isLight={isLight}>
              <Donut
                data={metrics.policies.effectMix}
                colors={policyColors}
                isLight={isLight}
                center={compactCount(metrics.policies.allow + metrics.policies.deny + metrics.policies.requireApproval)}
                hint="policies"
              />
            </ChartCard>
            <ChartCard title="Policy status" hint="Active, draft, and disabled" isLight={isLight}>
              <Donut
                data={metrics.policies.statusMix}
                colors={statusColors}
                isLight={isLight}
                center={compactCount(metrics.policies.active)}
                hint="active"
              />
            </ChartCard>
          </div>
        )}
      </Section>

      <Section
        id="security-audit"
        n={9}
        title="Security and audit logs"
        hint="Decision mix and execution volume over time. No per-user history."
        isLight={isLight}
      >
        {decisionTotal === 0 ? (
          <MiniEmpty isLight={isLight} title="No audit events" description="No tool executions, denials, or failures were recorded in this range." />
        ) : (
          <div className="grid xl:grid-cols-12 gap-4">
            <div className="xl:col-span-4">
              <ChartCard title="Decisions" hint={metrics.rangeLabel} isLight={isLight}>
                <Donut data={decisionSlices} colors={decisionColors} isLight={isLight} center={compactCount(decisionTotal)} hint="calls" />
              </ChartCard>
            </div>
            <div className="xl:col-span-8">
              <ChartCard title="Decisions over time" hint="Allowed, denied, approval, and failed" isLight={isLight}>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={metrics.audit.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} interval={xInterval} minTickGap={16} />
                      <YAxis tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Legend />
                      <Area type="monotone" dataKey="allowed" name="Allowed" stackId="dec" stroke={decisionColors[0]} fill={decisionColors[0]} fillOpacity={0.28} />
                      <Area type="monotone" dataKey="denied" name="Denied" stackId="dec" stroke={decisionColors[1]} fill={decisionColors[1]} fillOpacity={0.28} />
                      <Area
                        type="monotone"
                        dataKey="approval_required"
                        name="Approval"
                        stackId="dec"
                        stroke={decisionColors[2]}
                        fill={decisionColors[2]}
                        fillOpacity={0.28}
                      />
                      <Area type="monotone" dataKey="failed" name="Failed" stackId="dec" stroke={decisionColors[3]} fill={decisionColors[3]} fillOpacity={0.28} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
          </div>
        )}
      </Section>

      <Section
        id="integrations"
        n={10}
        title="Integrations"
        hint="Connected MCP servers, published tools, and supported AI clients"
        isLight={isLight}
      >
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <MetricCard
            label="AI clients"
            value={compactCount(metrics.integrations.clients.length)}
            hint={metrics.integrations.byStatus.map((row) => `${row.value} ${row.name.toLowerCase()}`).join(' · ')}
            isLight={isLight}
          />
          <MetricCard
            label="Published tools"
            value={compactCount(metrics.integrations.publishedTools)}
            hint={`${metrics.integrations.publishedResources} resources · ${metrics.integrations.publishedPrompts} prompts`}
            isLight={isLight}
          />
          <MetricCard
            label="External MCP servers"
            value={compactCount(metrics.integrations.external.length)}
            hint={`${metrics.integrations.external.reduce((sum, row) => sum + row.tools, 0)} remote tools`}
            isLight={isLight}
          />
          <MetricCard
            label="Client types"
            value={compactCount(metrics.integrations.byType.length)}
            hint="Supported AI clients in this filter"
            isLight={isLight}
          />
        </div>
        <div className="grid xl:grid-cols-12 gap-4">
          <div className="xl:col-span-4">
            <ChartCard title="Clients by type" hint="Supported AI clients" isLight={isLight}>
              {clientTypeSlices.length === 0 ? (
                <p className={`text-sm ${muted}`}>No AI clients in this filter.</p>
              ) : (
                <Donut
                  data={clientTypeSlices}
                  colors={modelColors}
                  isLight={isLight}
                  center={compactCount(metrics.integrations.clients.length)}
                  hint="clients"
                />
              )}
            </ChartCard>
          </div>
          <div className="xl:col-span-4">
            <ChartCard title="Clients by status" hint="Active, disabled, and revoked" isLight={isLight}>
              <Donut
                data={metrics.integrations.byStatus}
                colors={statusColors}
                isLight={isLight}
                center={compactCount(metrics.integrations.byStatus.find((row) => row.key === 'active')?.value || 0)}
                hint="active"
              />
            </ChartCard>
          </div>
          <div className="xl:col-span-4">
            <ChartCard title="External MCP tools" hint="Remote tools by connected server" isLight={isLight}>
              {metrics.integrations.external.length === 0 ? (
                <p className={`text-sm ${muted}`}>No external MCP servers are connected.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics.integrations.external} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Bar dataKey="tools" name="Tools" radius={[0, 6, 6, 0]} barSize={16} fill={indigo} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>
        </div>
      </Section>
    </div>
  );
}
